/**
 * Account → Workspace (web.md §4): storage (this browser, kept after closing?, size), Export workspace
 * (`workspace.export` → a `digester-workspace` JSON file), Load sample data, Import seed file
 * (`workspace.importSeed`), Reset workspace (`workspace.reset`, after confirmation) — and, when signed in,
 * Delete account: a code is emailed (`POST /api/account/delete/request`), confirming it deletes the
 * account on the server (`…/delete/confirm`) and signs this browser out; local data stays.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DigesterError, toDigesterError, type SeedData, type SeedReport } from '@digester/core';
import { openDialog } from '../../app/dialogs.js';
import { reportError } from '../../app/errors.js';
import { useServices } from '../../app/services.js';
import { showToast } from '../../app/Toast.js';
import { useEngine, useEngineQuery, useEngineStatus } from '../../engine-client/EngineProvider.js';
import { qk } from '../../engine-client/keys.js';
import { Icon } from '../../icons/Icon.js';
import { signOut } from '../../platform/session.js';
import { formatExact } from '../../ui/format.js';
import { Section } from '../../ui/form.js';
import { loadSampleData } from '../../account/sample.js';
import { formatBytes, readTextFile, saveJsonFile, useAccountState } from '../../account/shared.js';
import { EditorFrame } from '../shell/index.js';

const MAX_SEED_FILE = 2_000_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Shape check of a seed file (the engine validates every entry). */
export function parseSeedFile(text: string): SeedData {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new DigesterError('invalid_input', 'Choose a Digester seed JSON file');
  }
  if (!isRecord(data) || !Array.isArray(data.topics) || !Array.isArray(data.sources)) throw new DigesterError('invalid_input', 'Choose a Digester seed JSON file');
  return data as unknown as SeedData;
}

function seedSummary(report: SeedReport): string {
  const parts = [`${report.topicsCreated} ${report.topicsCreated === 1 ? 'topic' : 'topics'}`, `${report.sourcesCreated} ${report.sourcesCreated === 1 ? 'source' : 'sources'}`];
  return `Imported ${parts.join(' and ')}${report.skipped.length ? ` · ${report.skipped.length} skipped` : ''}`;
}

export function WorkspaceEditor() {
  const engine = useEngine();
  const { api, session } = useServices();
  const account = useAccountState();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { durable } = useEngineStatus();
  const stats = useEngineQuery(qk.workspace('stats'), (e) => e.workspace.stats(), { refetchOnMount: 'always' });
  const seedInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<{ text: string; details: string[] } | null>(null);
  const [deleteStep, setDeleteStep] = useState<'idle' | 'code'>('idle');
  const [deleteCode, setDeleteCode] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const refreshStats = (): void => void queryClient.invalidateQueries({ queryKey: qk.workspace() });

  const run = async (what: string, task: () => Promise<void>): Promise<void> => {
    setBusy(what);
    try {
      await task();
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(null);
      refreshStats();
    }
  };

  const exportWorkspace = (): Promise<void> =>
    run('export', async () => {
      const data = await engine.workspace.export();
      saveJsonFile(`digester-workspace-${new Date(data.exportedAt).toISOString().slice(0, 10)}.json`, data);
      showToast('Workspace exported');
    });

  const loadSample = (): Promise<void> =>
    run('sample', async () => {
      const report = await loadSampleData(engine);
      await engine.settings.setLocal('onboarded', true);
      setResult({ text: `Sample data loaded: ${report.messages} messages, ${report.sources} sources, ${report.topics} topics, ${report.filters} filters`, details: report.skipped.map((s) => `${s} — needs Pro`) });
      showToast('Sample data loaded');
    });

  const importSeed = (file: File): Promise<void> =>
    run('seed', async () => {
      const seed = parseSeedFile(await readTextFile(file, MAX_SEED_FILE).catch(() => Promise.reject(new DigesterError('invalid_input', 'The seed file is too large'))));
      const report = await engine.workspace.importSeed(seed);
      setResult({ text: seedSummary(report), details: report.skipped.map((s) => `${s.entry} — ${s.reason}`) });
      showToast(seedSummary(report));
    });

  const reset = (): void => {
    openDialog('confirm', {
      title: 'Reset workspace',
      message: 'Delete every source, message, topic, filter and setting in this browser?',
      note: account.signedIn ? 'Only this browser is reset. With sync on, your other devices and your account keep their data.' : 'This cannot be undone. Export the workspace first if you want a copy.',
      confirmLabel: 'Reset workspace',
      onConfirm: () =>
        run('reset', async () => {
          await engine.workspace.reset();
          setResult(null);
          showToast('Workspace reset');
        }),
    });
  };

  const requestDeletion = async (): Promise<void> => {
    setDeleteError(null);
    await run('delete', async () => {
      await api.call('accountDeleteRequest', { body: {} });
      setDeleteStep('code');
      setDeleteCode('');
    });
  };

  const confirmDeletion = async (): Promise<void> => {
    setBusy('delete');
    setDeleteError(null);
    try {
      await api.call('accountDeleteConfirm', { body: { code: deleteCode.trim() } });
      await signOut(session);
      setDeleteStep('idle');
      showToast('Account deleted · this browser keeps its data');
    } catch (error) {
      const e = toDigesterError(error);
      setDeleteError(e.code === 'invalid_input' || e.code === 'unauthenticated' || e.code === 'forbidden' ? 'That code is not right or has expired.' : e.message);
    } finally {
      setBusy(null);
    }
  };

  const s = stats.data;
  return (
    <EditorFrame title="Workspace" icon="settings">
      <Section title="Workspace">
        <div className="read-only-row">
          <span>Storage</span>
          <strong>This browser</strong>
        </div>
        <div className="read-only-row">
          <span>Kept after closing</span>
          <strong data-durable>{durable === false || s?.durable === false ? 'No — this browser clears it when the tab closes' : 'Yes'}</strong>
        </div>
        <div className="read-only-row">
          <span>Size</span>
          <strong>{s ? formatBytes(s.bytes) : '—'}</strong>
        </div>
        <div className="read-only-row">
          <span>Messages</span>
          <strong data-messages>{s ? formatExact(s.messages) : '—'}</strong>
        </div>
        <div className="workspace-actions">
          <button type="button" className="secondary-button" disabled={busy !== null} onClick={() => void exportWorkspace()}>
            <Icon name="download" /> Export workspace
          </button>
          <button type="button" className="secondary-button" disabled={busy !== null} onClick={() => void loadSample()}>
            <Icon name={busy === 'sample' ? 'loader' : 'sparkles'} /> Load sample data
          </button>
          <button type="button" className="secondary-button" disabled={busy !== null} onClick={() => seedInput.current?.click()}>
            <Icon name="upload" /> Import seed file
          </button>
          <input
            ref={seedInput}
            type="file"
            accept="application/json,.json"
            hidden
            data-seed-input
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void importSeed(file);
            }}
          />
          <button type="button" className="secondary-button danger-button" disabled={busy !== null} onClick={reset}>
            <Icon name="rotate-ccw" /> Reset workspace
          </button>
        </div>
        {result ? (
          <div className="test-result workspace-result" role="status">
            <Icon name="check" />
            {result.text}
            {result.details.length ? (
              <ul className="field-help">
                {result.details.slice(0, 12).map((d) => (
                  <li key={d}>{d}</li>
                ))}
                {result.details.length > 12 ? <li>and {result.details.length - 12} more</li> : null}
              </ul>
            ) : null}
          </div>
        ) : null}
      </Section>
      {account.signedIn ? (
        <Section title="Delete account" help="Deletes your account, subscription and synced copy on the server.">
          <p className="field-help panel-lead">
            This browser keeps its data and becomes a Free workspace. To confirm, we email a code to <strong>{account.email}</strong>.
          </p>
          {deleteStep === 'idle' ? (
            <div className="panel-actions">
              <button type="button" className="secondary-button danger-button" disabled={busy !== null} onClick={() => void requestDeletion()}>
                <Icon name="trash" /> Delete account…
              </button>
            </div>
          ) : (
            <>
              <label className="field delete-code">
                <span>Code from the email</span>
                <input
                  name="deleteCode"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={deleteCode}
                  onChange={(e) => setDeleteCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </label>
              {deleteError ? (
                <p className="form-error" role="alert">
                  {deleteError}
                </p>
              ) : null}
              <div className="panel-actions">
                <button type="button" className="secondary-button" disabled={busy !== null} onClick={() => setDeleteStep('idle')}>
                  Cancel
                </button>
                <button type="button" className="primary-button danger-fill" disabled={busy !== null || deleteCode.length !== 6} onClick={() => void confirmDeletion()}>
                  Delete account
                </button>
              </div>
            </>
          )}
        </Section>
      ) : (
        <Section title="Account">
          <p className="field-help panel-lead">This workspace has no account. Everything stays in this browser.</p>
          <div className="panel-actions">
            <button type="button" className="secondary-button" onClick={() => navigate('/signin?next=/settings/account/workspace')}>
              <Icon name="log-in" /> Sign in
            </button>
          </div>
        </Section>
      )}
    </EditorFrame>
  );
}
